import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { i18n } from '@tickitacka/shared';
import { useAuthStore } from '@/stores/authStore';

/**
 * 날짜 범위를 포맷팅하는 함수
 * @param startDate 시작 날짜 (Date | string | null)
 * @param endDate 종료 날짜 (Date | string | null)
 * @param format 날짜 포맷 (기본값: 'MM.DD')
 * @returns 포맷팅된 날짜 문자열
 *
 * @example
 * // 단일 날짜
 * formatDateRange('2025-01-01', '2025-01-01') // '01.01'
 *
 * // 날짜 범위
 * formatDateRange('2025-01-01', '2025-01-05') // '01.01 ~ 01.05'
 *
 * // 시작 날짜만 있는 경우
 * formatDateRange('2025-01-01', null) // '01.01'
 *
 * // 날짜가 없는 경우
 * formatDateRange(null, null) // ''
 */
export const formatDateRange = (
    startDate: Date | string | null,
    endDate: Date | string | null,
    format = 'MM.DD'
): string => {
    if (!startDate && !endDate) return '';

    const start = startDate ? dayjs(startDate) : null;
    const end = endDate ? dayjs(endDate) : null;

    if (!start && end) return end.format(format);
    if (start && !end) return start.format(format);
    if (start && end) {
        if (start.isSame(end)) {
            return start.format(format);
        }
        return `${start.format(format)} ~ ${end.format(format)}`;
    }

    return '';
};

/**
 * 프로젝트, 목표의 마감기한 등을 표시할 때 사용하는 기간 포맷팅 함수
 * @param startDate 시작 날짜 (Date | string | null)
 * @param endDate 종료 날짜 (Date | string | null)
 * @returns 날짜 범위를 포맷팅한 문자열
 */
export const dateRangeDisplay = (
    startDate: Date | string | null,
    endDate: Date | string | null
) => {
    return endDate
        ? startDate
            ? formatDateRange(startDate, endDate)
            : formatDateRange(endDate, null)
        : '-';
};

/**
 * 날짜를 비교하여 오늘/어제/내일을 판단하는 유틸리티 함수
 */
export function getRelativeDateText(date: Date): string | null {
    const today = new Date();
    const todayDateString = today.toDateString();
    const dateString = date.toDateString();

    // 오늘 날짜 확인
    if (dateString === todayDateString) {
        return i18n.t('date_picker.today');
    }

    // 내일 날짜 확인
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (dateString === tomorrow.toDateString()) {
        return i18n.t('date_picker.tomorrow');
    }

    // 어제 날짜 확인
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dateString === yesterday.toDateString()) {
        return i18n.t('date_picker.yesterday');
    }

    return null;
}

/**
 * 날짜 리스트 중 필터 기간에 포함되어있는지 확인
 * @param dates 날짜 리스트
 * @param startDate 필터 기간 시작 날짜
 * @param endDate 필터 기간 종료 날짜
 * @returns 필터 기간에 포함된 날짜 리스트
 */
export function filterDatesByPeriod(
    dates: Date[],
    startDate: Date,
    endDate: Date
): Date[] {
    return dates.filter(date => date >= startDate && date <= endDate);
}

/**
 * 다음 실행 시각을 계산하는 함수
 */
export function getNextExecutionTime(selectedDays: number[], time: string) {
    const { user } = useAuthStore.getState();

    // 현재 사용자의 시간대 기준 시간 가져오기
    const userTimezone = user?.timezone || 'Asia/Seoul';
    const now = dayjs().tz(userTimezone);

    // 1. 현재 요일 구하기 (0=일요일, 6=토요일)
    const currentWeekday = now.day();

    // 2. 시간 파싱
    const [hour, minute] = time.split(':').map(Number);

    // 3. 오늘 설정 시간 계산
    const todayScheduledTime = now
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0);

    // 4. 현재 시간이 오늘 설정 시간보다 이전이고, 오늘 요일이 선택된 요일에 포함되는지 확인
    if (
        now.isBefore(todayScheduledTime) &&
        selectedDays.includes(currentWeekday)
    ) {
        // 오늘이 선택된 요일이고 현재 시각이 설정 시간보다 이전이면 오늘로 설정
        return todayScheduledTime.utc().format();
    }

    // 5. 다음 실행 요일 찾기
    let nextWeekday = selectedDays.find(day => day > currentWeekday);
    let daysToAdd = 0;

    // 6. 다음 주로 넘어가는 경우
    if (nextWeekday === undefined) {
        // 선택된 요일 중 가장 작은 값
        nextWeekday = Math.min(...selectedDays);
        daysToAdd = 7 - currentWeekday + nextWeekday;
    } else {
        daysToAdd = nextWeekday - currentWeekday;
    }

    // 7. 다음 실행 시간 계산
    const nextExecutionTime = now
        .add(daysToAdd, 'day')
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0);

    // 8. UTC로 변환
    const nextExecutionTimeUTC = nextExecutionTime.utc().format();

    return nextExecutionTimeUTC;
}

/**
 * 데일리 스크럼 전용 날짜 포맷팅 함수
 * "어제, 2025년 6월 11일" / "오늘, 2025년 6월 12일" / "내일, 2025년 6월 13일" 형식으로 표시
 * 상대적 날짜가 아닌 경우 "3월 2일 (금)" 형식으로 표시
 * @param date 포맷팅할 Date 객체
 * @param options 날짜 포맷 옵션
 * @returns 포맷팅된 날짜 문자열
 */
export function formatDateForDailyScrum(
    date: Date,
    options?: Intl.DateTimeFormatOptions
): string {
    const locale = i18n.language || 'ko-KR';

    // 기존 getRelativeDateText 함수 활용
    const relativeText = getRelativeDateText(date);

    if (relativeText) {
        // 상대적 날짜 표현(어제, 오늘, 내일)이 있는 경우
        if (options) {
            // 사용자 지정 옵션이 있으면 해당 옵션으로 포맷팅
            const formattedDate = date.toLocaleDateString(locale, options);
            return `${relativeText}, ${formattedDate}`;
        } else {
            // 기본적으로는 상대적 날짜만 표시
            return relativeText;
        }
    } else {
        // 상대적 날짜가 아닌 경우 "3월 2일 (금)" 형식으로 표시
        if (options) {
            // 사용자 지정 옵션이 있으면 해당 옵션 사용
            return date.toLocaleDateString(locale, options);
        } else {
            // 기본 옵션: "3월 2일 (금)" 형식
            const defaultOptions: Intl.DateTimeFormatOptions = {
                month: 'long',
                day: 'numeric',
                weekday: 'short',
            };
            return date.toLocaleDateString(locale, defaultOptions);
        }
    }
}
